import React, { useMemo } from 'react';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';

const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

const formatCurrency = (val) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(val || 0);

export default function YearMonthNav({
  items = [],
  dateField = 'date',
  totalField = 'total',
  selectedYear,
  setSelectedYear,
  selectedMonth,
  setSelectedMonth,
}) {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-based

  const monthlyData = useMemo(() => {
    const data = Array.from({ length: 12 }, () => ({ count: 0, total: 0 }));
    items.forEach((item) => {
      const raw = item[dateField];
      if (!raw) return;
      const d = raw.length > 10 ? new Date(raw) : new Date(raw + 'T00:00:00');
      if (d.getFullYear() !== selectedYear) return;
      const m = d.getMonth();
      data[m].count += 1;
      if (totalField && item[totalField] != null) {
        data[m].total += Math.abs(parseFloat(item[totalField]) || 0);
      }
    });
    return data;
  }, [items, selectedYear, dateField, totalField]);

  const availableYears = useMemo(() => {
    const years = new Set();
    items.forEach((item) => {
      const raw = item[dateField];
      if (!raw) return;
      const d = raw.length > 10 ? new Date(raw) : new Date(raw + 'T00:00:00');
      years.add(d.getFullYear());
    });
    years.add(currentYear);
    return [...years].sort();
  }, [items, dateField, currentYear]);

  const handleMonthClick = (monthIdx) => {
    setSelectedMonth(selectedMonth === monthIdx ? null : monthIdx);
  };

  return (
    <div className="border border-[#E2E8F0] rounded-xl bg-white overflow-hidden" data-testid="year-month-nav">
      {/* Year selector row */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#F8FAFC] border-b border-[#E2E8F0]">
        <button
          onClick={() => setSelectedYear(selectedYear - 1)}
          disabled={!availableYears.includes(selectedYear - 1) && selectedYear - 1 < Math.min(...availableYears)}
          className="p-1 rounded hover:bg-[#E2E8F0] text-[#4A5568] disabled:opacity-30 transition-colors"
          data-testid="year-prev"
        >
          <CaretLeft size={18} weight="bold" />
        </button>
        <span className="font-outfit font-bold text-[#0033A0] text-lg" data-testid="year-label">
          {selectedYear}
        </span>
        <button
          onClick={() => setSelectedYear(selectedYear + 1)}
          disabled={selectedYear >= currentYear}
          className="p-1 rounded hover:bg-[#E2E8F0] text-[#4A5568] disabled:opacity-30 transition-colors"
          data-testid="year-next"
        >
          <CaretRight size={18} weight="bold" />
        </button>
      </div>

      {/* Months row - scrollable on mobile, grid on desktop */}
      <div className="overflow-x-auto scrollbar-hide">
        <div className="grid grid-cols-12 divide-x divide-[#E2E8F0] min-w-[600px] sm:min-w-0">
          {MONTHS.map((name, idx) => {
            const d = monthlyData[idx];
            const isCurrentMonth = selectedYear === currentYear && idx === currentMonth;
            const isSelected = selectedMonth === idx;
            const hasData = d.count > 0;

            return (
              <button
                key={idx}
                onClick={() => handleMonthClick(idx)}
                className={`
                  flex flex-col items-center py-2.5 px-1 transition-all cursor-pointer text-center min-w-0
                  ${isSelected ? 'bg-[#0033A0]/10 ring-1 ring-inset ring-[#0033A0]/30' : ''}
                  ${isCurrentMonth && !isSelected ? 'bg-green-50' : ''}
                  ${!isSelected && !isCurrentMonth ? 'hover:bg-[#F4F6F8]' : ''}
                `}
                data-testid={`month-${idx}`}
              >
                <span className={`text-[10px] font-semibold uppercase tracking-wide mb-1 ${isSelected ? 'text-[#0033A0]' : isCurrentMonth ? 'text-green-700' : 'text-[#4A5568]'}`}>
                  {name}
                </span>
                <span className={`text-[10px] ${hasData ? 'font-semibold text-[#0A0F1A]' : 'text-[#A0AEC0]'}`}>
                  {d.count} doc
                </span>
                {totalField && (
                  <span className={`text-[10px] mt-0.5 font-medium ${hasData ? 'text-green-700' : 'text-[#A0AEC0]'}`}>
                    {hasData ? formatCurrency(d.total) : '0 \u20AC'}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
